/**
 * Backbone model for listing and selecting CommCare menus (modules, forms, and cases)
 */
import $ from "jquery";
import _ from "underscore";
import Sentry from "sentry_browser";
import initialPageData from "hqwebapp/js/initial_page_data";
import toggles from "hqwebapp/js/toggles";
import Collections from "cloudcare/js/formplayer/menus/collections";
import constants from "cloudcare/js/formplayer/constants";
import errors from "cloudcare/js/form_entry/errors";
import formEntryUtils from "cloudcare/js/form_entry/utils";
import FormplayerFrontend from "cloudcare/js/formplayer/app";
import AppsAPI from "cloudcare/js/formplayer/apps/api";
import UsersModels from "cloudcare/js/formplayer/users/models";
import formplayerUtils from "cloudcare/js/formplayer/utils/utils";
import ProgressBar from "cloudcare/js/formplayer/layout/views/progress_bar";
import cloudcareUtils from "cloudcare/js/utils";

let currentSelections = null,
    ongoingRequests = [];

var API = {
    queryFormplayer: function (params, route) {
        var user = UsersModels.getCurrentUser(),
            timezoneOffsetMillis = (new Date()).getTimezoneOffset() * 60 * 1000 * -1,
            tzFromBrowser = Intl.DateTimeFormat().resolvedOptions().timeZone,
            windowWidth = String(window.innerWidth),
            keepAPMTraces = toggles.toggleEnabled('ACTIVATE_DATADOG_APM_TRACES'),
            formplayerUrl = user.formplayer_url,
            displayOptions = user.displayOptions || {},
            defer = $.Deferred(),
            options,
            menus;

        $.when(AppsAPI.getAppEntities()).done(function (appCollection) {
            var appIdType;
            const app = appCollection.find(function (app) {
                const currPrimaryAppId = app.get('copy_of');
                // Prevents breaking if a web apps session spans pre and post deploy. Can remove post deploy.
                if (app.id && app.id === params.appId) {
                    appIdType = 'build';
                    return app;
                }
                if (currPrimaryAppId && currPrimaryAppId === params.appId) {
                    appIdType = 'canonical';
                    return app;
                }
                if (currPrimaryAppId && (currPrimaryAppId === params.copyOf)) {
                    appIdType = 'copyOf';
                    return app;
                }
            });
            const additionalSentryData = {
                appIdType: appIdType,
                appIDsFromURL: params.appId + ' / ' + params.copyOf,
                appCollection: appCollection.models.map(
                    appItem => appItem.attributes._id + ' / ' + appItem.attributes.copy_of),
            };
            var errorMsg = 'The application could not be found.';
            if (params.appId) {
                // Likely due to a link followed from an old build.
                errorMsg = errorMsg + ' If you clicked on a link, that link may be outdated.';
            }
            if (!params.preview) {
                // Make sure the user has access to the app
                if (!app) {
                    FormplayerFrontend.trigger(
                        'showError',
                        gettext(errorMsg),
                        false,
                        true,
                        additionalSentryData,
                    );
                    FormplayerFrontend.trigger('navigateHome');
                    defer.reject();
                    return;
                }
            }
            let appId = params.appId;
            if (!params.preview) {
                const copyOf = app.get('copy_of');
                if (copyOf && copyOf === params.appId) {
                    appId = app.id;
                }
            }
            FormplayerFrontend.permitIntervalSync = true;
            options = {
                success: function (parsedMenus, response) {
                    if (response.status === 'retry') {
                        FormplayerFrontend.trigger('retry', response, function () {
                            var newOptionsData = JSON.stringify($.extend(true, { mustRestore: true }, JSON.parse(options.data)));
                            menus.fetch($.extend(true, {}, options, { data: newOptionsData }));
                        }, gettext('Waiting for server progress'));
                    } else if (_.has(response, 'exception')) {
                        FormplayerFrontend.trigger('clearProgress');
                        if (params.clickedIcon && response.statusCode === 404) {
                            parsedMenus.removeCaseRow = true;
                            defer.resolve(parsedMenus);
                        } else {
                            FormplayerFrontend.trigger(
                                'showError',
                                response.exception,
                                response.type === 'html',
                            );

                            var currentUrl = FormplayerFrontend.getCurrentRoute();
                            if (FormplayerFrontend.lastError === currentUrl) {
                                FormplayerFrontend.lastError = null;
                                FormplayerFrontend.trigger('navigateHome');
                            } else {
                                FormplayerFrontend.lastError = currentUrl;
                                FormplayerFrontend.trigger('navigation:back');
                            }
                            defer.reject();
                        }
                    } else {
                        if (response.smartLinkRedirect) {
                            if (user.isAppPreview) {
                                FormplayerFrontend.trigger('showSuccess', gettext("You have selected a case in a different domain. App Preview does not support this feature.", 5000));
                                FormplayerFrontend.trigger('navigateHome');
                                return;
                            }

                            // Drop last selection to avoid redirect loop if user presses back in the future
                            var urlObject = formplayerUtils.currentUrlToObject();
                            urlObject.setSelections(_.initial(urlObject.selections || []));
                            formplayerUtils.setUrlToObject(urlObject, true);

                            console.log("Redirecting to " + response.smartLinkRedirect);
                            document.location = response.smartLinkRedirect;
                            return;
                        }

                        let attemptRestore = undefined;
                        if (parsedMenus.metaData) {
                            attemptRestore = parsedMenus.metaData.attemptRestore;
                        }
                        formplayerUtils.setSyncInterval(params.appId, attemptRestore);
                        sessionStorage.setItem("lastUserActivityTime",  Date.now());
                        FormplayerFrontend.trigger('clearProgress');
                        defer.resolve(parsedMenus);
                        // Only configure menu debugger if we didn't get a form entry response
                        if (!(response.session_id) && (UsersModels.getCurrentUser().isAppPreview
                                || !cloudcareUtils.smallScreenIsEnabled())) {
                            FormplayerFrontend.trigger('configureDebugger');
                        }
                    }
                },
                error: function (_, response) {
                    if (response.status === 423) {
                        FormplayerFrontend.trigger(
                            'showError',
                            errors.LOCK_TIMEOUT_ERROR,
                        );
                    } else if (response.status === 401) {
                        FormplayerFrontend.trigger(
                            'showError',
                            formEntryUtils.reloginErrorHtml(),
                            true,
                        );
                    } else if (response.statusText === 'abort') {
                        // do nothing
                    } else {
                        FormplayerFrontend.trigger(
                            'showError',
                            gettext('Unable to connect to form playing service. ' +
                                    'Please report an issue if you continue to see this message.'),
                        );
                    }
                    var urlObject = formplayerUtils.currentUrlToObject();
                    if (urlObject.selections) {
                        urlObject.selections.pop();
                        formplayerUtils.setUrlToObject(urlObject);
                    }
                    defer.reject();
                },
            };
            var casesPerPage = parseInt($.cookie("cases-per-page-limit"))
                || (window.innerWidth <= constants.SMALL_SCREEN_WIDTH_PX ? 5 : 10);
            const data = {
                "username": user.username,
                "restoreAs": user.restoreAs,
                "domain": user.domain,
                "app_id": appId,
                "endpoint_id": params.endpointId,
                "endpoint_args": params.endpointArgs,
                "locale": params.changeLang || displayOptions.language,
                "selections": params.selections,
                "offset": params.page * casesPerPage,
                "search_text": params.search,
                "form_session_id": params.sessionId,
                "query_data": params.queryData,
                "cases_per_page": casesPerPage,
                "oneQuestionPerScreen": displayOptions.oneQuestionPerScreen,
                "isPersistent": params.isPersistent,
                "sortIndex": params.sortIndex,
                "preview": params.preview,
                "tz_offset_millis": timezoneOffsetMillis,
                "tz_from_browser": tzFromBrowser,
                "selected_values": params.selectedValues,
                "isShortDetail": params.isShortDetail,
                "isRefreshCaseSearch": params.isRefreshCaseSearch,
                "requestInitiatedByTag": params.requestInitiatedByTag,
                "windowWidth": windowWidth,
                "keepAPMTraces": keepAPMTraces,
            };
            options.data = JSON.stringify(data);
            options.url = formplayerUrl + '/' + route;

            menus = Collections();

            if (Object.freeze) {
                Object.freeze(options);
            }
            const sentryData = _.pick(data, ["selections", "query_data", "app_id"]);
            Sentry.addBreadcrumb({
                category: "formplayer",
                message: "[request] " + route,
                data: _.pick(sentryData, _.identity),
            });

            var callStartTime = performance.now();
            const updateRequest = menus.fetch($.extend(true, {}, options));

            if (route.startsWith("navigate_menu")) {
                if (!_.isEqual(params.selections, currentSelections)) {
                    currentSelections = params.selections;
                    while (ongoingRequests.length > 0) {
                        const ongoingRequest = ongoingRequests.pop();
                        if (ongoingRequest.readyState !== 4) {
                            ongoingRequest.abort();
                        }
                    }
                }
                ongoingRequests.push(updateRequest);
            }


            updateRequest.always(function () {
                if (data.requestInitiatedByTag && data.requestInitiatedByTag === constants.requestInitiatedByTagsMapping.DYNAMIC_SEARCH) {
                    var callEndTime = performance.now();
                    var callResponseTime = callEndTime - callStartTime;
                    $.ajax(initialPageData.reverse('api_histogram_metrics'), {
                        method: 'POST',
                        data: {responseTime: callResponseTime, metrics: "commcare.dynamic_search.response_time"},
                        error: function () {
                            console.log("API call failed to record metrics");
                        },
                    });
                }
            });
        });

        return defer.promise();
    },
};

FormplayerFrontend.getChannel().reply("app:select:menus", function (options) {
    if (sessionStorage.selectedValues !== undefined) {
        const currentSelectedValues = JSON.parse(sessionStorage.selectedValues)[sessionStorage.queryKey];
        options.selectedValues = currentSelectedValues !== undefined && currentSelectedValues !== '' ? currentSelectedValues.split(',') : undefined;
    }
    if (!options.endpointId) {
        return API.queryFormplayer(options, options.isInitial ? "navigate_menu_start" : "navigate_menu");
    }

    var progressView = ProgressBar({
        progressMessage: gettext("Loading..."),
    });
    FormplayerFrontend.regions.getRegion('loadingProgress').show(progressView);

    // If an endpoint is provided, first claim any cases it references, then navigate
    return API.queryFormplayer(options, "get_endpoint");
});

FormplayerFrontend.getChannel().reply("icon:click", function (options) {
    return API.queryFormplayer(options, "get_endpoint");
});

FormplayerFrontend.getChannel().reply("entity:get:details", function (options, isPersistent, isShortDetail, isRefreshCaseSearch) {
    options.isPersistent = isPersistent;
    options.preview = UsersModels.getCurrentUser().displayOptions.singleAppMode;
    options.isShortDetail = isShortDetail;
    options.isRefreshCaseSearch = isRefreshCaseSearch;
    return API.queryFormplayer(options, 'get_details');
});

export default API;
